import React, { useEffect, useMemo, useState } from 'react';
import { Button, List, Modal, Upload, Progress, Card, Tag, Empty, Collapse } from 'antd';
import { DeleteOutlined, EyeOutlined, UploadOutlined, FileTextOutlined } from '@ant-design/icons';
import { deleteNurseDocument, getNurseDocumentTypes, listNurseDocuments, uploadNurseDocument } from '../../../api';
import { useToast } from '../../../ui/ToastProvider';
import { auth, storage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from '../../../firebase';

const { Dragger } = Upload;

const DOCUMENT_DEFINITIONS = [
  { docType: "Resume", category: "application", onboarding: true },
  { docType: "Photo", category: "application", onboarding: false },
  { docType: "ID", category: "compliance", onboarding: false },
  { docType: "RN License", category: "compliance", onboarding: false },
  { docType: "RN Registration", category: "compliance", onboarding: false },
  { docType: "BLS", category: "compliance", onboarding: false },
  { docType: "ACLS", category: "compliance", onboarding: false },
  { docType: "PALS", category: "compliance", onboarding: false },
  { docType: "NRP", category: "compliance", onboarding: false },
  { docType: "NIHSS", category: "compliance", onboarding: false },
  { docType: "TNCC", category: "compliance", onboarding: false },
  { docType: "Infection Control", category: "compliance", onboarding: false },
  { docType: "Skills Checklist", category: "compliance", onboarding: false }
];

const getDocumentConfig = (docType) => {
  const def = DOCUMENT_DEFINITIONS.find(d => d.docType === docType);
  return {
    key: docType,
    icon: <FileTextOutlined />,
    color: '#004d54',
    label: docType,
    required: def ? def.onboarding : false
  };
};

const DocumentsStep = ({ onFinish, onBack }) => {
  const [types, setTypes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState({ active: false, docType: null, percent: 0 });
  const [selectedDocType, setSelectedDocType] = useState(null);
  const { notify } = useToast();

  const loadEverything = async () => {
    try {
      const [t, d] = await Promise.all([getNurseDocumentTypes(), listNurseDocuments()]);
      setTypes(DOCUMENT_DEFINITIONS.map(d => d.docType));
      setDocs(d.data || []);
    } catch (e) {
      notify({ title: 'Failed to load documents', description: 'Please try again later.', type: 'error' });
    }
  };

  useEffect(() => {
    loadEverything();
  }, []);

  const makeUploadProps = (docType) => ({
    name: 'file',
    multiple: false,
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError, onProgress }) => {
      try {
        setUploading({ active: true, docType, percent: 0 });
        const user = auth.currentUser;
        if (!user) {
          throw new Error('No authenticated user');
        }
        const timestamp = Date.now();
        const storagePath = `private/user/${user.uid}/${docType}_${timestamp}_${file.name}`;
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed', (snapshot) => {
          const percent = parseFloat(((snapshot.bytesTransferred / snapshot.totalBytes) * 100).toFixed(2));
          onProgress && onProgress({ percent });
          setUploading((prev) => ({ ...prev, percent }));
        }, (err) => {
          onError && onError(err);
        }, async () => {
          try {
            // Notify backend of uploaded file mapping
            await uploadNurseDocument({ fileName: storagePath, docType, rewrite: true });
            onSuccess && onSuccess({}, file);
            notify({ title: 'Upload complete', description: `${docType} uploaded successfully.`, type: 'success' });
            loadEverything();
          } catch (e) {
            onError && onError(e);
          } finally {
            setUploading({ active: false, docType: null, percent: 0 });
          }
        });
      } catch (e) {
        onError && onError(e);
        notify({ title: 'Upload failed', description: 'Please try again.', type: 'error' });
        setUploading({ active: false, docType: null, percent: 0 });
      }
    },
    onProgress: ({ percent }) => {
      setUploading((prev) => ({ ...prev, percent: Math.round(percent || 0) }));
    },
  });

  const handleDelete = async (fileName, docType) => {
    Modal.confirm({
      title: 'Delete document?',
      onOk: async () => {
        try {
          await deleteNurseDocument({ fileName, docType });
          notify({ title: 'Deleted', description: `${docType} removed.`, type: 'success' });
          loadEverything();
        } catch (e) {
          notify({ title: 'Delete failed', description: 'Please try again.', type: 'error' });
        }
      }
    });
  };

  const getCleanFileName = (fileName) => {
    if (!fileName) return '';
    try {
      const parts = fileName.split('/');
      return parts[parts.length - 1];
    } catch (_) {
      return fileName;
    }
  };

  const getShortDisplayName = (fileName) => {
    const clean = getCleanFileName(fileName);
    const lastDot = clean.lastIndexOf('.');
    let base = clean;
    let ext = '';
    if (lastDot > 0) {
      base = clean.slice(0, lastDot);
      ext = clean.slice(lastDot + 1).toLowerCase();
    }
    const isTruncated = base.length > 10;
    const shortBase = base.slice(0, 10) + (isTruncated ? '...' : '');
    return ext ? `${shortBase}.${ext}` : shortBase;
  };

  const items = useMemo(() => types.map((docType) => {
    const existing = (docs || []).find((d) => d.docType === docType);
    const cfg = getDocumentConfig(docType);
    return { docType, cfg, existing };
  }), [types, docs]);

  const requiredDocTypes = useMemo(() => (types || []).filter((t) => getDocumentConfig(t).required), [types]);
  const numRequiredUploaded = useMemo(
    () => requiredDocTypes.filter((t) => (docs || []).some((d) => d.docType === t)).length,
    [requiredDocTypes, docs]
  );
  const allRequiredUploaded = requiredDocTypes.length === 0 || numRequiredUploaded === requiredDocTypes.length;

  const requiredItems = useMemo(() => items.filter(i => i.cfg.required), [items]);
  const optionalItems = useMemo(() => items.filter(i => !i.cfg.required), [items]);

  const renderListItem = ({ docType, cfg, existing }) => (
    <List.Item
      actions={[
        existing ? (
          <>
            <Button key="delete" danger icon={<DeleteOutlined />} onClick={async () => {
              try {
                await deleteNurseDocument({ fileName: existing.fileName, docType });
                await deleteObject(ref(storage, existing.fileName));
                notify({ title: 'Deleted', description: `${docType} removed.`, type: 'success' });
                loadEverything();
              } catch (e) {
                notify({ title: 'Delete failed', description: 'Please try again.', type: 'error' });
              }
            }}>Delete</Button>
          </>
        ) : (
          <Upload key="upload" {...makeUploadProps(docType)}>
            <Button icon={<UploadOutlined />} loading={uploading.active && uploading.docType === docType} style={{ backgroundColor: '#004d54', color: '#fff', borderColor: '#004d54' }}>Upload</Button>
          </Upload>
        )
      ]}
    >
      <List.Item.Meta
        avatar={<span style={{ display: 'inline-flex', width: 32, height: 32, borderRadius: 6, alignItems: 'center', justifyContent: 'center', background: '#F0F5FF' }}>{cfg.icon}</span>}
        title={
          <div className="doc-title-row">
            <span className="doc-title">{cfg.label}</span>
            {cfg.required ? <Tag color="red" className="doc-required-tag">Required</Tag> : null}
          </div>
        }
        description={existing ? <span className="doc-file-name">{getShortDisplayName(existing.fileName)}</span> : 'No file uploaded'}
      />
      {(uploading.active && uploading.docType === docType) ? (
        <div style={{ width: 180 }}>
          <Progress percent={uploading.percent} size="small" />
        </div>
      ) : null}
    </List.Item>
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.2 }}>Documents</div>
        <div style={{ color: '#667085' }}>Upload the required credentials to complete your profile.</div>
      </div>
      <style>{`
        .doc-title-row { display: flex; align-items: center; gap: 8px; flex-wrap: nowrap; overflow: hidden; }
        .doc-title { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .doc-required-tag { flex: 0 0 auto; }
        .doc-file-name { display: inline-block; max-width: 240px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ant-collapse-ghost > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box { padding-top: 0; padding-bottom: 0; }
        @media (max-width: 480px) {
          .doc-file-name { max-width: 160px; }
        }
      `}</style>
      {items.length === 0 ? (
        <Empty description="No document types available" />
      ) : (
        <>
          {requiredItems.length > 0 && (
            <div style={{ marginBottom: 0 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1f2937' }}>Required Documents</h3>
              <List
                itemLayout="horizontal"
                dataSource={requiredItems}
                renderItem={renderListItem}
              />
            </div>
          )}
          
          {optionalItems.length > 0 && (
            <Collapse 
              ghost 
              style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}
              expandIconPosition="end"
            >
              <Collapse.Panel 
                header={<span style={{ fontWeight: 600, color: '#334155' }}>Optional Documents</span>} 
                key="1"
              >
                <List
                  itemLayout="horizontal"
                  dataSource={optionalItems}
                  renderItem={renderListItem}
                />
              </Collapse.Panel>
            </Collapse>
          )}
        </>
      )}
      <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#667085', fontSize: 13, fontWeight: 500 }}>
            {requiredDocTypes.length > 0 ? `${numRequiredUploaded}/${requiredDocTypes.length} required documents uploaded` : null}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {onBack ? (
            <Button onClick={onBack} type="text" icon={<div style={{ marginRight: 6 }}>←</div>} style={{ color: '#667085', fontSize: 16 }}>Back</Button>
          ) : <div />}
          <Button
            type="primary"
            onClick={() => {
              if (!allRequiredUploaded) {
                notify({ title: 'Missing documents', description: 'Please upload all required documents.', type: 'error' });
                return;
              }
              onFinish();
            }}
            style={{
              height: 48,
              padding: '0 32px',
              fontSize: 16,
              borderRadius: 8,
              backgroundColor: '#004d54',
              borderColor: '#004d54',
              boxShadow: '0 4px 12px rgba(0, 77, 84, 0.15)',
              opacity: !allRequiredUploaded ? 0.6 : 1
            }}
          >
            Finish
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentsStep;


